import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useLazyQuery } from "@apollo/react-hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import accountIcon from "@iconify/icons-mdi/account";
import keyIcon from "@iconify/icons-mdi/key";
import { InlineIcon } from "@iconify/react";
import Validator from "fastest-validator";
import { SERVICE_LOGIN } from "my-hr/graphql";
import useOcProperty from "oc/hooks/use-property";

interface MyHRServiceLoginPageProps {
  onLogin: (loginResult: any) => void;
}

export const MyHRServiceLoginPage: React.FC<MyHRServiceLoginPageProps> = ({
  onLogin,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  /** Employee code states */
  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeCodeError, setEmployeeCodeError] = useState<boolean>(false);
  const [employeeCodeErrorText, setEmployeeCodeErrorText] = useState<string>("");

  /** Password states */
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorText, setPasswordErrorText] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);

  /** Button state */
  const [submitButtonState, setSubmitButtonState] = useState(false);

  /** Validator stuff */
  const validator = new Validator();
  const schema = {
    employeeCode: {
      type: "string",
      min: 1,
      messages: {
        string: t("MY_HR_ERROR_MESSAGE_EMPLOYEE_CODE_REQUIRED"),
        stringMin: t("MY_HR_ERROR_MESSAGE_EMPLOYEE_CODE_REQUIRED"),
      },
    },
    password: {
      type: "string",
      min: 1,
      messages: {
        string: "Nem megfelelő jelszó!",
        stringMin: t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_REQUIRED"),
      },
    },
  };
  const check = validator.compile(schema);

  const [login, { data: loginData, error: loginError }] = useLazyQuery(
    SERVICE_LOGIN,
    {
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    if (loginData?.serviceLogin) {
      onLogin(loginData.serviceLogin);
      let { from } = (location.state as { from?: { pathname: string } }) || {
        from: { pathname: "/" },
      };
      setSubmitButtonState(false);
      history.replace(from || { pathname: "/" });
    }
  }, [loginData, onLogin, location.state, history]);

  useEffect(() => {
    if (loginError?.graphQLErrors) {
      loginError.graphQLErrors.forEach((item) => {
        if (item.message === "Invalid employee code!") {
          setEmployeeCodeErrorText(t("MY_HR_ERROR_MESSAGE_INVALID_EMPLOYEE_CODE"));
          setEmployeeCodeError(true);
        }
        if (item.message === "No valid legal contract found!") {
          setEmployeeCodeErrorText(t("MY_HR_ERROR_MESSAGE_NO_CONTRACT_FOUND"));
          setEmployeeCodeError(true);
        }
        if (item.message === "No user found!") {
          setEmployeeCodeErrorText(t("MY_HR_ERROR_MESSAGE_NO_USER_FOUND"));
          setEmployeeCodeError(true);
        }
        if (item.message === "Wrong password!") {
          setPasswordErrorText(t("MY_HR_ERROR_MESSAGE_WRONG_PASSWORD"));
          setPasswordError(true);
        }
      });
      setSubmitButtonState(false);
    }
  }, [loginError, t]);

  const { data: passwordVisibilityData } = useOcProperty({
    resource: "system",
    key: "passwordVisibility",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let isValidated = await check({ employeeCode, password });
    if (isValidated !== true) {
      isValidated.forEach((item: any) => {
        if (item.field === "employeeCode") {
          setEmployeeCodeErrorText(item.message);
          setEmployeeCodeError(true);
        }
        if (item.field === "password") {
          setPasswordErrorText(item.message);
          setPasswordError(true);
        }
      });
      return;
    }
    setSubmitButtonState(true);
    login({ variables: { employeeCode, password } });
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      width="100%"
      height="100%"
      style={{ backgroundColor: "#cc0033" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Box
        width="400px"
        height="100%"
        style={{ backgroundColor: "white" }}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Box id="orgware-text-logo">
          <img
            alt="orgware-icon"
            src="/img/owLogoColored.png"
            style={{ width: 300 }}
          />
        </Box>
        <Typography
          variant="body1"
          style={{ color: "#cc0033", marginTop: "32px" }}
        >
          Service login
        </Typography>
        <form noValidate autoComplete="off" onSubmit={handleSubmit}>
          <Box width="304px" paddingTop="38px">
            <TextField
              id="employeeCode"
              defaultValue={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value)}
              label={t("MY_HR_EMPLOYEE_CODE")}
              variant="outlined"
              fullWidth
              error={employeeCodeError}
              onFocus={() => {
                setEmployeeCodeErrorText("");
                setEmployeeCodeError(false);
              }}
              helperText={employeeCodeError && employeeCodeErrorText}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InlineIcon height="24px" icon={accountIcon} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="password"
              defaultValue={password}
              onChange={(e) => setPassword(e.target.value)}
              label={t("MY_HR_PASSWORD")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              fullWidth
              helperText={passwordErrorText}
              error={passwordError}
              onFocus={() => {
                setPasswordErrorText("");
                setPasswordError(false);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InlineIcon height="24px" icon={keyIcon} />
                    <Box width="8px" />
                    {passwordVisibilityData === "true" && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleShowPassword}
                        style={{
                          backgroundColor: "#F1F3F4",
                          height: "48px",
                          width: "48px",
                        }}
                      >
                        {showPassword ? (
                          <VisibilityIcon
                            style={{ height: "24px", width: "24px" }}
                          />
                        ) : (
                          <VisibilityOffIcon
                            style={{ height: "24px", width: "24px" }}
                          />
                        )}
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              style={{ marginTop: "36px" }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disableElevation
              disabled={submitButtonState}
              style={{
                marginTop: "36px",
              }}
            >
              {t("MY_HR_LOGIN")}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default MyHRServiceLoginPage;
